import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import mainApi from "../../../assets/api/MainApi";
import { ArrowIcon, CloseIcon } from "../../../assets/icons/icons";
import MiniPreloader from "../../MiniPreloader/MiniPreloader";

import "./MenuPopup.css";
import { CATALOG_MAIN_LINK } from "../../../assets/utils/constants";
import { ConfigContext } from "../../../assets/contexts/configContext";
import { getCorrectGoodsWordForm } from "../../../assets/utils/utils";

function MenuPopup({ isOpened, setOpened, categories }) {
  const [selectedCategory, setSelectCategory] = useState(undefined);
  const [isPreloaderVisible, setPreloaderVisible] = useState(false);
  const [subcategories, setSubcategories] = useState(undefined);
  const [subcategoriesCache, setSubcategoriesCache] = useState({});
  const requestIdRef = useRef(0);

  const config = useContext(ConfigContext);
  const { initialConstants } = config;
  const { SHOP_ID_FOR_API } = initialConstants;

  function close() {
    setOpened(false);
    setTimeout(() => {
      setOpened(false);
    }, 1);
  }

  const navigate = useNavigate();
  const location = useLocation();
  const pathParts = location.pathname.split("/").filter(Boolean);
  const currentCategorySlug =
    pathParts[0] === CATALOG_MAIN_LINK && pathParts[1]
      ? pathParts[1]
      : undefined;
  const currentSubcategorySlug =
    pathParts[0] === CATALOG_MAIN_LINK && pathParts[2]
      ? pathParts[2]
      : undefined;
  function handleSelectCategory(item, { skipNav = false } = {}) {
    if (!item) return;

    if (selectedCategory && selectedCategory._id === item._id && !skipNav) {
      navigate(`/${CATALOG_MAIN_LINK}/${item.translit_name}`);
      close();
      return;
    }

    if (item.is_final) {
      setSelectCategory(item);
      setSubcategories([]);
      if (!skipNav) {
        navigate(`/${CATALOG_MAIN_LINK}/${item.translit_name}`);
        close();
      }
      return;
    }

    setSelectCategory(item);
    setSubcategories(undefined);

    if (subcategoriesCache[item._id]) {
      setSubcategories(subcategoriesCache[item._id]);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    setPreloaderVisible(true);
    mainApi
      .getCategories({
        limit: 25,
        shop_id: SHOP_ID_FOR_API,
        _id: item._id,
      })
      .then((res) => {
        if (currentRequestId !== requestIdRef.current) return;
        setSubcategories(res.data);
        setSubcategoriesCache((prev) => ({ ...prev, [item._id]: res.data }));
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        if (currentRequestId === requestIdRef.current) {
          setPreloaderVisible(false);
        }
      });
  }

  useEffect(() => {
    if (!isOpened) {
      setSelectCategory(undefined);
      setSubcategories(undefined);
      setPreloaderVisible(false);
      requestIdRef.current += 1;
    }
  }, [isOpened]);

  useEffect(() => {
    if (!isOpened || selectedCategory || !categories || categories.length === 0)
      return;

    const current = currentCategorySlug
      ? categories.find((cat) => cat.translit_name === currentCategorySlug)
      : undefined;
    const fallback = categories.find((cat) => !cat.is_final);
    const target = current || fallback;
    if (!target) return;
    handleSelectCategory(target, { skipNav: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpened, categories, currentCategorySlug]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") close();
    };
    if (isOpened) {
      document.addEventListener("keydown", handleEsc);
    }
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpened]);

  function useViewportHeight() {
    const [maxHeight, setMaxHeight] = useState(window.innerHeight);

    useEffect(() => {
      function calculateHeight() {
        const div = document.createElement("div");
        div.style.height = "env(safe-area-inset-bottom)";
        div.style.position = "fixed";
        div.style.bottom = "0";
        div.style.visibility = "hidden";

        document.body.appendChild(div);

        const safeAreaBottom = parseFloat(getComputedStyle(div).height) || 0;

        document.body.removeChild(div);

        setMaxHeight(window.innerHeight - safeAreaBottom - 70);
      }

      calculateHeight();
      window.addEventListener("resize", calculateHeight);

      return () => window.removeEventListener("resize", calculateHeight);
    }, []);

    return maxHeight;
  }
  const maxHeight = useViewportHeight();

  return (
    <>
      {categories && categories.length > 0 && Array.isArray(categories) ? (
        <div
          className={`menu-popup__container ${isOpened ? "menu-popup__container_active" : ""
            }`}
          onClick={(e) => {
            if (e.target.classList.contains("menu-popup__container")) close();
          }}
        >
          <div
            className={`menu-popup ${isOpened ? "menu-popup_active" : "menu-popup_inactive"
              }`}
            style={{
              maxHeight: maxHeight
            }}
          >
            <div className="menu-popup__top">
              <div className="menu-popup__titles">
                <p className="menu-popup__eyebrow">Каталог</p>
                <p className="menu-popup__subtitle">
                  Найдите нужный раздел и переходите к покупкам.
                </p>
              </div>
              <button className="menu-popup__close" type="button" onClick={close}>
                <CloseIcon
                  mainClassName={"menu-popup__close-icon"}
                  fillClassName={"menu-popup__close-icon-fill"}
                />
              </button>
            </div>

            <div className="menu-popup__cards">
              <AnimatePresence initial={false}>
                {categories.map((item, i) => {
                  const isSelected = selectedCategory && selectedCategory._id === item._id;
                  return (
                    <motion.div
                      layout
                      key={`menu-popup__card${i}${item._id}`}
                      className="menu-popup__card-box"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.18 }}
                    >
                      <button
                        className={`menu-popup__card ${isSelected ? "menu-popup__card_selected" : ""
                          }`}
                        type="button"
                        onClick={() => {
                          handleSelectCategory(item);
                        }}
                      >
                        <div className="menu-popup__card-info">
                          <p className="menu-popup__card-name">{item.name}</p>
                          {item.count ? (
                            <span className="menu-popup__card-count">{item.count} {getCorrectGoodsWordForm(item.count)}</span>
                          ) : null}
                        </div>
                        {!item.is_final ? (
                          <ArrowIcon
                            mainClassName={"menu-popup__card-arrow"}
                            fillClassName={"menu-popup__card-arrow-fill"}
                          />
                        ) : null}
                      </button>
                      <AnimatePresence initial={false}>
                        {isSelected ? (
                          isPreloaderVisible ? (
                            <div className="menu-popup__card-preloader">
                              <MiniPreloader />
                            </div>
                          ) : subcategories && subcategories.length > 0 ? (
                            <motion.div
                              key={`subcats_${selectedCategory._id}`}
                              className="menu-popup__card-subcategories"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              {subcategories.map((sub, i) => (
                                <NavLink
                                  key={`menu-popup__card-subcategory${i}${sub._id}`}
                                  className={({ isActive }) =>
                                    `menu-popup__card-subcategory ${isActive ? "menu-popup__card-subcategory_active" : ""
                                    }`
                                  }
                                  to={`/${CATALOG_MAIN_LINK}/${selectedCategory.translit_name}/${sub.translit_name}`}
                                  onClick={close}
                                >
                                  {sub.name}
                                </NavLink>
                              ))}
                              <Link
                                className="menu-popup__card-open"
                                to={`/${CATALOG_MAIN_LINK}/${selectedCategory.translit_name}`}
                                onClick={close}
                              >
                                Перейти к товарам
                              </Link>
                            </motion.div>
                          ) : null
                        ) : null}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default MenuPopup;
